import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useAuth } from "@/context/AuthContext";
import { hasClientPrivacy } from "@/lib/client-privacy";
import { canUseStaffPage } from "@/lib/page-permissions";
import {
  syncPendingOps,
  getPendingCount,
  subscribePendingCount,
  subscribeSyncStatus,
  warmCache,
  getLastSynced,
  subscribeLastSynced,
  subscribeCacheWarming,
} from "@/lib/sync";

const OfflineContext = createContext(null);

export function OfflineProvider({ children }) {
  const { user } = useAuth();
  const privacyRestricted = hasClientPrivacy(user);
  const canUseClientCache = user?.user_type === "staff" && canUseStaffPage(user, "clients") && !privacyRestricted;
  const isOnline = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [syncStatus, setSyncStatus] = useState("idle");
  const [lastSynced, setLastSynced] = useState(null);
  const [isCacheWarming, setIsCacheWarming] = useState(false);

  useEffect(() => {
    getPendingCount().then(setPendingCount);
    getLastSynced().then(setLastSynced);
    const unsub1 = subscribePendingCount(setPendingCount);
    const unsub2 = subscribeSyncStatus(setSyncStatus);
    const unsub3 = subscribeLastSynced(setLastSynced);
    const unsub4 = subscribeCacheWarming(setIsCacheWarming);
    return () => { unsub1(); unsub2(); unsub3(); unsub4(); };
  }, []);

  // Warm the cache in the background on first load when online.
  // This does NOT block rendering — it runs after mount.
  useEffect(() => {
    if (isOnline && canUseClientCache) {
      warmCache();
    }
  }, [isOnline, canUseClientCache]);

  useEffect(() => {
    if (isOnline && canUseClientCache && pendingCount > 0) {
      syncPendingOps();
    }
  }, [isOnline, canUseClientCache]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (syncStatus === "synced") {
      const t = setTimeout(() => setSyncStatus("idle"), 3000);
      return () => clearTimeout(t);
    }
  }, [syncStatus]);

  const triggerSync = useCallback(() => {
    if (isOnline && canUseClientCache) syncPendingOps();
  }, [isOnline, canUseClientCache]);

  const triggerWarmCache = useCallback(() => {
    if (isOnline && canUseClientCache) warmCache();
  }, [isOnline, canUseClientCache]);

  return (
    <OfflineContext.Provider value={{
      isOnline,
      pendingCount,
      syncStatus,
      triggerSync,
      lastSynced,
      isCacheWarming,
      privacyRestricted,
      triggerWarmCache,
    }}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  return useContext(OfflineContext);
}
